<template>
  <div class="content">
    <Loader v-if="$apolloData.loading" />
    <div class="title-text">
      <p class="title">Tracking Pixels</p>
      <p class="subtitle">
        Manage your tracking pixels and pixel settings.
      </p>
    </div>
    <div class="content-body">
      <div class="card">
        <div class="card-content">
          <div class="card-title">
            <i class="fa fa-user-robot-xmarks"></i> Bot Block
          </div>
          <p>
            <strong
              >If enabled, we will not send pixel events from identified
              bots.</strong
            >
            All views and clicks will be reported on ArtistHub, but you may see
            less events on your ad platform.
          </p>
          <p>
            We've identified that 10-16% of paid traffic (Meta / TikTok Ads,
            etc.) is from bots. By not sending bot events, it's easier for ad
            platforms to identify your target audience and make your ads more
            cost effective.
          </p>
          <p>
            If you are optimizing for Landing Page Views, turning on Bot Block
            may increase conversion cost. This is normal as Bot Block will
            prevent false-positives.
          </p>
        </div>
        <div v-if="blockBots !== null" class="card-footer">
          <button
            @click="setBotBlock()"
            :class="blockBots ? 'button-danger' : 'button-default'"
          >
            <span>{{ blockButtonText }}</span>
          </button>
        </div>
      </div>
      <div class="is-agency" v-if="isAgency">
        <div
          class="artist"
          @click="tab = 'artist'"
          :class="{ active: tab === 'artist' }"
        >
          {{ artist.name }}
        </div>
        <div
          class="agency"
          @click="tab = 'agency'"
          :class="{ active: tab === 'agency' }"
        >
          Agency
        </div>
      </div>
      <div class="card" v-if="tabPixels.length">
        <div class="card-content">
          <div class="card-title">Pixels</div>
          <div>
            <div class="pixels-table">
              <table>
                <thead>
                  <tr class="head">
                    <th>
                      <div class="th-content">
                        <span>Provider</span>
                      </div>
                    </th>
                    <th>
                      <div class="th-content">
                        <span>Pixel</span>
                      </div>
                    </th>
                    <th>
                      <div class="th-content api">
                        <span>API</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="pixel in tabPixels"
                    :key="pixel.provider"
                    class="row"
                  >
                    <td class="provider">
                      <span>{{ pixel.provider }}</span>
                    </td>
                    <td class="pixel">
                      <span>{{ pixel.pixel }}</span>
                    </td>
                    <td class="api">
                      <div
                        class="icon-container"
                        v-if="['Facebook', 'Snapchat'].includes(pixel.provider)"
                      >
                        <font-awesome-icon
                          :icon="[
                            'far',
                            pixel.valid ? 'circle-check' : 'circle-xmark'
                          ]"
                          :class="{ invalid: !pixel.valid }"
                          v-if="Object.keys(pixel).includes('valid')"
                        />
                        <span v-else>Loading...</span>
                      </div>
                    </td>
                    <td class="actions">
                      <div class="actions-container">
                        <button @click="deletePixel(pixel)">
                          <span>Delete</span>
                          <RemoveIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-content">
          <div class="add-pixel">
            <div class="card-title">Add pixel</div>
            <div class="providers">
              <span>Providers</span>
              <div class="remaining">
                <div
                  class="provider"
                  v-for="(provider, i) in allProviders"
                  :key="i"
                  :class="{ selected: provider === selectedProvider }"
                  @click="selectedProvider = provider"
                >
                  {{ provider }}
                </div>
              </div>
              <div class="inputs">
                <div class="social-input">
                  <label for="pixel_id" v-if="selectedProvider !== 'AdWords'">
                    Pixel ID
                  </label>
                  <label for="pixel_id" v-else>Tag ID</label>
                  <div class="input-container">
                    <input
                      v-model="pixel_id"
                      type="text"
                      id="pixel_id"
                      placeholder="Paste the pixel ID"
                    />
                  </div>
                </div>
                <div class="social-input" v-if="selectedProvider !== 'AdWords'">
                  <label for="pixel_token">API Access Token</label>
                  <div class="input-container">
                    <input
                      v-model="pixel_token"
                      type="text"
                      id="pixel_token"
                      placeholder="Paste the access token"
                    />
                  </div>
                </div>
              </div>
              <div class="actions">
                <button
                  class="button-default"
                  @click="savePixel"
                  :disabled="
                    !pixel_id ||
                      (!pixel_token && selectedProvider !== 'AdWords') ||
                      !selectedProvider
                  "
                >
                  Add Pixel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <SnackBar />
  </div>
</template>

<script>
import {
  M_INSERT_PIXEL,
  Q_PIXELS,
  M_DELETE_PIXEL,
  Q_BOT_BLOCK,
  M_UPDATE_BOT_BLOCK
} from "@/graphql";
import SnackBar from "@/components/Custom/SnackBar";
import Swal from "sweetalert2";
import "@/assets/scss/custom/vendors-extensions/_sweetalert.scss";
import Loader from "@/components/Custom/Loader";
import RemoveIcon from "../../../../public/img/icons/remove.svg";

export default {
  name: "TrackingPixels",
  components: { SnackBar, Loader, RemoveIcon },
  data() {
    return {
      blockBots: null,
      artistPixels: [],
      agencyPixels: [],
      tiktokPixel: {},
      allProviders: ["Facebook", "TikTok", "AdWords", "Snapchat"],
      selectedProvider: "",
      pixel_id: "",
      pixel_token: "",
      tab: "artist"
    };
  },
  apollo: {
    $subscribe: {
      bot_block_status: {
        query: Q_BOT_BLOCK,
        variables() {
          return {
            artist_id: this.artist.id
          };
        },
        result({ data }) {
          this.blockBots = data.artists_by_pk.block_bots;
        }
      }
    },
    pixels: {
      query: Q_PIXELS,
      variables() {
        return {
          payload: {
            _or: [
              {
                artist_id: {
                  _eq: this.artist.id
                }
              },
              {
                agency_id: {
                  _eq: this.artist.billing_user_id
                }
              }
            ]
          }
        };
      },
      async result({ data }) {
        const pixels = await Promise.all(
          data.pixels.map(async pixel => {
            let valid = false;
            if (pixel.provider === "Facebook") {
              valid = await this.testFBPixel(pixel.pixel, pixel.access_token);
            } else if (pixel.provider === "Snapchat") {
              valid = await this.testSnapchatPixel(
                pixel.pixel,
                pixel.access_token
              );
            }

            return { ...pixel, valid };
          })
        );

        this.artistPixels = pixels.filter(p => p.artist_id === this.artist.id);
        this.agencyPixels = pixels.filter(p => p.agency_id === this.artist.billing_user_id);
      }
    }
  },
  async created() {
    if (!this.subscriptionActive(this.artist)) {
      // TODO: Re-enable after fixing subs
      // await this.$router.push({ name: "ArtistSettings" });
    }
  },
  computed: {
    artist() {
      return this.$store.getters.getArtist;
    },
    user() {
      return this.$store.getters.getUser;
    },
    snack() {
      return this.$store.getters.getSnack;
    },
    blockButtonText() {
      if (this.blockBots) {
        return "Disable Bot Block";
      } else {
        return "Enable Bot Block";
      }
    },
    tabPixels() {
      return this.tab === "artist" ? this.artistPixels : this.agencyPixels;
    },
    isAgency() {
      return this.user.plan === "agency" || !!this.user.master_account_id;
    }
  },
  methods: {
    async savePixel() {
      try {
        const pixel = {
          provider: this.selectedProvider,
          pixel: this.pixel_id,
          access_token: this.pixel_token,
          conversion_tag:
            this.selectedProvider === "AdWords" ? this.pixel_id : null
        };

        if (this.tab === "agency") {
          pixel.agency_id = this.artist.billing_user_id;
        } else {
          pixel.artist_id = this.artist.id;
        }

        const result = await this.$apollo.mutate({
          mutation: M_INSERT_PIXEL,
          variables: { pixel }
        });

        console.log("savePixel result", result);

        await this.$store.dispatch("snack", {
          text: "Success! Pixel has been updated!"
        });

        this.pixel_id = "";
        this.pixel_token = "";
        this.selectedProvider = "";

        return this.$apollo.queries.pixels.refetch();
      } catch (err) {
        console.log(err);
        await this.$store.dispatch("snack", {
          type: "error",
          text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
        });
      }
    },
    async testFBPixel(pixel, access_token) {
      try {
        const response = await fetch(
          `https://graph.facebook.com/v12.0/${pixel}/events?access_token=${access_token}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              data: [
                {
                  event_name: "Healthcheck",
                  event_time: Math.floor(Date.now() / 1000),
                  event_source_url: document.location.href,
                  user_data: { external_id: 0 },
                  action_source: "other"
                }
              ]
            })
          }
        );

        return response.status === 200;
      } catch (err) {
        console.log(err);
        return false;
      }
    },
    async testSnapchatPixel(pixel, access_token) {
      try {
        const response = await fetch(
          "https://tr.snapchat.com/v2/conversion/validate",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${access_token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              data: [{ pixel_id: pixel }]
            })
          }
        );

        return response.status === 200;
      } catch (err) {
        console.log(err);
        return false;
      }
    },
    async deletePixel(pixel) {
      const confirmDelete = await Swal.fire({
        title: "Delete this pixel?",
        type: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Yes, Delete Pixel",
        cancelButtonText: "No, keep it",
        confirmButtonClass: "button-danger big",
        cancelButtonClass: "button-secondary big",
        buttonsStyling: false
      });

      if (confirmDelete.value) {
        try {
          await this.$apollo.mutate({
            mutation: M_DELETE_PIXEL,
            variables: {
              id: pixel.id
            }
          });

          if (this.tab === "agency") {
            this.agencyPixels = this.agencyPixels.filter(
              p => p.id !== pixel.id
            );
          } else {
            this.artistPixels = this.artistPixels.filter(
              p => p.id !== pixel.id
            );
          }

          await this.$store.dispatch("snack", {
            text: "Success! Pixel deleted."
          });
        } catch (err) {
          console.log(err);
          await this.$store.dispatch("snack", {
            type: "error",
            text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
          });
        }
      }
    },
    async setBotBlock() {
      try {
        this.blockBots = !this.blockBots;

        await this.$apollo.mutate({
          mutation: M_UPDATE_BOT_BLOCK,
          variables: {
            artist_id: this.artist.id,
            block_bots: this.blockBots
          }
        });

        await this.$store.dispatch("snack", {
          text: `Bot Block has been ${
            this.artist.block_bots ? "disabled" : "enabled"
          }`
        });
      } catch (err) {
        console.log(err);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.is-agency {
  display: flex;
  justify-content: center;
  margin: 24px 0;
  .artist,
  .agency {
    padding: 12px 24px;
    cursor: pointer;
    font-size: 16px;
    line-height: 17px;
    color: white;
    background: #24224a;
    width: 50%;
    border-bottom: 2px solid #9c27b0;
    &.active {
      background: #9c27b0;
    }
  }
  .artist {
    border-top-left-radius: 10px;
  }
  .agency {
    border-top-right-radius: 10px;
  }
}
.card {
  margin-bottom: 36px;
  /* BG/type2 */
  background: #24224a;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  .card-title {
    /* 18/SemiBold */
    font-weight: 600;
    font-size: 18px;
    line-height: 30px;
    /* White */
    color: #ffffff;
    mix-blend-mode: normal;
    opacity: 0.9;
  }
  .card-content {
    justify-content: center;
    .pixels-table {
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 6px;
        margin-top: 6px;
        thead {
          tr {
            th {
              padding: 18px 18px 6px 0;
              /* 12/SemiBold */
              font-weight: 600;
              font-size: 12px;
              line-height: 15px;
              /* Text/type0 */
              color: $text-type-0;
              &:first-child {
                padding-left: 18px;
              }
              .th-content {
                display: flex;
                align-items: center;
                &.api {
                  padding-left: 6px;
                }
              }
              svg {
                margin-left: 5px;
                path {
                  fill: $text-type-0;
                }
              }
            }
          }
        }
        tbody {
          tr {
            /* BG/type1 */
            background: $bg-type-1;
            //border-bottom: 6px solid $bg-type-2;
            .provider {
              border-radius: 10px 0 0 10px;
              padding-left: 18px;
              span {
                /* 14/SemiBold */
                font-size: 14px;
                line-height: 28px;
                /* White */
                color: white;
                mix-blend-mode: normal;
                opacity: 0.9;
                &.you-text {
                  margin-left: 6px;
                  font-weight: 600;
                }
              }
            }
            .api {
              .invalid {
                color: red;
              }
            }
            .actions {
              .actions-container {
                display: flex;
                //justify-content: flex-end;
                button {
                  display: flex;
                  align-items: center;
                  background: none;
                  border: none;
                  margin-top: 0;
                  &:focus {
                    outline: none;
                  }
                  span {
                    color: $purple;
                    /* 14/SemiBold */
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 22px;
                  }
                  svg {
                    width: 18px;
                    margin-left: 8px;
                    path {
                      fill: $purple;
                    }
                  }
                }
              }
            }
            td {
              //padding: 18px;
              //border: solid 1px transparent;
              margin-bottom: 6px;
              /* 14/Regular */
              font-size: 14px;
              line-height: 28px;
              /* White */
              color: white;
              mix-blend-mode: normal;
              opacity: 0.9;
            }
          }
        }
      }
    }

    .add-pixel {
      .title {
        /* 14/SemiBold */
        font-weight: 600;
        font-size: 24px;
        line-height: 17px;
        /* Text/type2 */
        color: #ffffff;
      }

      .providers {
        margin-top: 24px;
        span {
          /* 14/SemiBold */
          font-weight: 600;
          font-size: 14px;
          line-height: 17px;
          /* Text/type2 */
          color: #59588d;
        }
        .remaining {
          margin: 6px 0 12px;
          display: flex;
          flex-wrap: wrap;
          .provider {
            margin-right: 6px;
            margin-bottom: 6px;
            padding: 12px 20px;
            border: 1px solid #b1afcd;
            box-sizing: border-box;
            border-radius: 8px;
            font-size: 16px;
            line-height: 17px;
            color: #ffffff;
            cursor: pointer;
            &.selected {
              border-color: #9c27b0;
            }
          }
        }
      }

      .inputs {
        display: flex;
        gap: 10px;
        .social-input {
          margin-top: 0;
        }
      }

      .actions {
        display: flex;
        align-items: center;
        justify-content: center;
        .button-default {
          margin-top: 12px;
          padding: 9px 30px;
        }
      }
    }
    .social-input {
      width: 100%;
      margin-top: 24px;
      label {
        margin-left: 11px;
        /* 12/Bold */
        font-weight: bold;
        font-size: 12px;
        line-height: 15px;
        /* Text/type1 */
        color: #8786ab;
      }
      .input-container {
        margin-top: 6px;
        display: flex;
        align-items: center;
        padding: 11px 13px;
        /* Purple-dark */
        background: #100e2a;
        /* Purple-stroke */
        border: 1px solid #312f62;
        box-sizing: border-box;
        border-radius: 5px;
        svg {
          width: 24px;
          margin-right: 10px;
        }
        .social-prefix {
          /* 14/Regular */
          font-size: 14px;
          line-height: 17px;
          /* Text/type2 */
          color: #59588d;
        }
        input {
          //margin-top: 6px;
          //padding: 13px 11px;
          //width: 100%;
          flex: 1;
          /* Purple-dark */
          background: #100e2a;
          /* Purple-stroke */
          //border: 1px solid #312F62;
          //box-sizing: border-box;
          //border-radius: 5px;
          outline: none;
          border: none;
          color: #b1afcd;
          /* 14/Regular */
          font-size: 14px;
          line-height: 17px;

          &:-webkit-autofill,
          &:-webkit-autofill:hover,
          &:-webkit-autofill:focus {
            border: none;
            -webkit-text-fill-color: $text-type-0;
            box-shadow: 0 0 0px 1000px $purple-dark inset;
            -webkit-box-shadow: 0 0 0px 1000px $purple-dark inset;
            transition: background-color 5000s ease-in-out 0s;
          }

          &:focus {
            outline: none;
            //border: 1px solid #9c27b0;
          }
          &::placeholder {
            color: #59588d;
            /* 14/Regular */
            font-size: 14px;
            line-height: 17px;
          }
        }
      }
    }
    button {
      margin-top: 18px;
      padding: 9px 1px;
      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
}
@media (max-width: 600px) {
  .card-content {
    .pixels-table {
      overflow: auto;
    }
    .inputs {
      flex-direction: column;
    }
  }
}
</style>
